import React from 'react';
import clsx from 'clsx';
import Toggle from '../shared/TailwindComponents/Toggle';

/**
 * One switchable row in the matter wizard - a legal source, a data source, an
 * analysis option. Every list of on/off choices in the create flow uses this,
 * so the Sources step and the Review step read the same way: a white
 * container on the grey form body, the label on the left, the switch on the
 * right. The switch carries the state; the row only tints its hairline, so a
 * page of defaults-on does not read as a page of highlighted cards.
 */
interface WizardToggleRowProps {
  label: string;
  description?: string;
  /** A small muted line under the label (document counts and the like). */
  meta?: string;
  icon?: React.ReactNode;
  enabled: boolean;
  onToggle: () => void;
  disabled?: boolean;
}

const WizardToggleRow: React.FC<WizardToggleRowProps> = ({
  label,
  description,
  meta,
  icon,
  enabled,
  onToggle,
  disabled,
}) => (
  <div
    className={clsx(
      'flex items-start justify-between gap-3 rounded-md border bg-surface p-3 transition-colors duration-200',
      enabled ? 'border-champagne/40' : 'border-hairline',
      disabled && 'opacity-50 pointer-events-none'
    )}
  >
    <div className="flex items-start gap-3 min-w-0">
      {icon && (
        <div
          className={clsx(
            'flex-shrink-0 mt-0.5 transition-colors duration-200 [&>svg]:h-5 [&>svg]:w-5',
            enabled ? 'text-fg-body' : 'text-fg-muted'
          )}
        >
          {icon}
        </div>
      )}
      <div className="min-w-0">
        <p className="text-sm font-medium text-fg-strong truncate" title={label}>
          {label}
        </p>
        {/* The title attribute carries the whole description - a translated
            sentence is routinely longer than the English it was sized for. */}
        {description && (
          <p className="text-xs text-fg-muted mt-0.5 line-clamp-2" title={description}>
            {description}
          </p>
        )}
        {meta && <p className="text-xs text-fg-muted mt-0.5">{meta}</p>}
      </div>
    </div>
    <div className="flex-shrink-0 mt-0.5">
      <Toggle enabled={enabled} onToggle={onToggle} disabled={disabled} ariaLabel={label} translate={false} />
    </div>
  </div>
);

export default WizardToggleRow;
